import { useEffect, useState } from "react";
import { Play, Loader2, Mail, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtDateTimeMtl } from "../../lib/dateMtl";

// Carte « Tournée du dimanche » (demande du propriétaire, 2026-10-16) :
// historique des récoltes automatiques de mots-clés (dates, mots ajoutés,
// totaux) + bouton « Lancer la tournée de 05 h maintenant » (admin seul) qui
// exécute sur demande ping moteurs + récolte + rapport courriel.
export default function MotsClesTourneeCard({ isAdmin, onChanged }) {
  const [runs, setRuns] = useState(null);
  const [busy, setBusy] = useState(false);

  const charge = async () => {
    try {
      const { data } = await api.get("/admin/seo/mots-cles-runs");
      setRuns(data.runs || []);
    } catch {
      setRuns([]);
    }
  };
  useEffect(() => { charge(); }, []);

  const lancer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/seo/tournee-maintenant");
      const m = data.mots_cles || {};
      toast.success(
        `Tournée terminée — ${(m.ajoutes_fr || []).length} mots-clés FR et `
        + `${(m.ajoutes_en || []).length} EN ajoutés · rapport ${m.courriel || "—"}`);
      await charge();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "La tournée a échoué — réessayez");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-surface border border-ivory/10 rounded-xl p-7 space-y-4" data-testid="mots-cles-tournee-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-semibold flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-gold" /> Tournée automatique du dimanche
          </p>
          <p className="text-xs text-ivory/70 mt-1 max-w-xl">
            Chaque dimanche vers 05 h : récolte des mots-clés les plus recherchés
            (Google Search Console + IA), ajout automatique aux listes ci-dessus,
            puis rapport par courriel à info@trophee.ca.
          </p>
        </div>
        {isAdmin && (
          <button type="button" data-testid="tournee-maintenant-btn" disabled={busy} onClick={lancer}
            className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Lancer la tournée de 05 h maintenant
          </button>
        )}
      </div>

      <div data-testid="mots-cles-runs" className="space-y-3">
        {runs === null && <p className="text-xs text-ivory/50">Chargement de l'historique…</p>}
        {runs?.length === 0 && (
          <p className="text-xs text-ivory/50">Aucune tournée encore — la première part dimanche à 05 h
            {isAdmin ? ", ou lancez-la maintenant avec le bouton ci-dessus." : "."}</p>
        )}
        {runs?.map((r, i) => (
          <div key={r.at || i} data-testid={`mots-cles-run-${i}`}
            className="bg-ink border border-ivory/10 rounded-lg px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="text-ivory font-medium">{r.at ? fmtDateTimeMtl(r.at) : "—"}</span>
              <span className="text-ivory/50">{r.gsc}</span>
              <span className="text-ivory/50">{r.gemini}</span>
              {r.courriel && (
                <span className="inline-flex items-center gap-1 text-ivory/50">
                  <Mail className="w-3 h-3" /> {r.courriel}
                </span>
              )}
              <span className="ml-auto text-gold">
                +{(r.ajoutes_fr || []).length} FR · +{(r.ajoutes_en || []).length} EN
                <span className="text-ivory/40"> (listes : {r.total_fr ?? "—"} FR · {r.total_en ?? "—"} EN)</span>
              </span>
            </div>
            {[...(r.ajoutes_fr || []).map((m) => [m, "fr"]), ...(r.ajoutes_en || []).map((m) => [m, "en"])].length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(r.ajoutes_fr || []).map((m, j) => (
                  <span key={`f${j}`} className="text-[10px] rounded-full px-2 py-0.5 border border-gold/40 text-gold bg-gold/10">{m}</span>
                ))}
                {(r.ajoutes_en || []).map((m, j) => (
                  <span key={`e${j}`} className="text-[10px] rounded-full px-2 py-0.5 border border-ivory/25 text-ivory/70 bg-surface">{m}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
